import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, TableBody, TableCell, TableRow, useTheme } from "@mui/material";
import NotFoundImg from "assets/images/not-found.png";
import { roundToTwo } from "utils/helpers";
import { OptimizationTypeEnum } from "types";
import { apiUrl } from "consts";
import { getContrastColor } from "theme/utils";
import type { IResultItem } from "../../types";
import { getFamilyImage } from "../../common/utils";
import Parameter from "../Parameter";
import getStyles from "./styles";

type Props = {
  tab: string;
  families: { data: { families: Array<{ id: string; image?: string }> } };
  description: string;
  options: IResultItem[];
  optimizationType: OptimizationTypeEnum;
  roadLength: number;
};

const LightingParameters: FC<Props> = ({ tab, families, description, options, optimizationType, roadLength }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Parameters, [theme]);

  const isLowestConsumption = optimizationType === OptimizationTypeEnum.LOWEST_CONSUMPTION;
  const isMaximumDistance = optimizationType === OptimizationTypeEnum.MAXIMUM_DISTANCE;

  return (
    <TableBody>
      <TableRow sx={styles.header}>
        <TableCell />
        {options.map((lum, index) => (
          <TableCell key={"TableCell" + index} scope="row" align="center">
            {tab === "data" && <Box component="img" src={getFamilyImage(lum.family_id, families)} alt={lum.name} sx={styles.img} />}
            {tab === "ldc" && <Box component="img" src={lum.e_lvk_img_url === "" ? NotFoundImg : `${apiUrl}${lum.e_lvk_img_url}`} alt="lvk" sx={styles.img} />}
            {tab === "descr" && <Box sx={styles.description}>{description || "-"}</Box>}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell>
          <Parameter label={t("Lampset")} />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum2" + index} scope="row" align="center">
            {lum.lampset}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell>
          <Parameter label={t("Luminous flux")} unit="(lm)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum3" + index} scope="row" align="center">
            {lum.LuminousFlux}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell>
          <Parameter label={t("Power Consumption")} unit="(W)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum4" + index} scope="row" align="center">
            {roundToTwo(lum.PowerConsumption)}
          </TableCell>
        ))}
      </TableRow>

      <TableRow sx={{ bgcolor: isLowestConsumption ? "var(--color-primary) !important" : "var(--color-white)" }}>
        <TableCell sx={{ color: isLowestConsumption ? "white !important" : "black" }}>
          <Parameter label={t("Load per km")} unit="(W/km)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell
            key={"lum5" + index}
            scope="row"
            align="center"
            sx={{ color: isLowestConsumption ? "var(--color-white) !important" : "var(--color-black)" }}
          >
            {lum.Load}
          </TableCell>
        ))}
      </TableRow>

      <TableRow sx={{ bgcolor: isMaximumDistance ? "var(--color-primary) !important" : "white" }}>
        <TableCell sx={{ color: isMaximumDistance ? getContrastColor() : getContrastColor() }}>
          <Parameter label={t("Distance between poles")} unit="(m)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell
            key={"lum6" + index}
            scope="row"
            align="center"
            sx={{ color: isMaximumDistance ? `${getContrastColor()} !important` : getContrastColor() }}
          >
            {lum.DistancePoles}
          </TableCell>
        ))}
      </TableRow>

      {roadLength > 0 && (
        <TableRow>
          <TableCell>
            <Parameter label={t("Number of luminaires")} unit={`(${roadLength} km)`} />
          </TableCell>
          {options.map((lum, index) => (
            <TableCell key={"lum5" + index} align="center" scope="row">
              {lum.nbr_luminaires ?? 0}
            </TableCell>
          ))}
        </TableRow>
      )}

      <TableRow>
        <TableCell>
          <Parameter label={t("Height of the light point")} unit="(m)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum7" + index} align="center" scope="row">
            {lum.Height}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell>
          <Parameter label={t("Overhang")} unit="(m)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum8" + index} align="center" scope="row">
            {lum.Overhang}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell>
          <Parameter label={t("Tilt of the luminaire")} unit="(°)" />
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"lum10" + index} align="center" scope="row">
            {lum.Tilt}
          </TableCell>
        ))}
      </TableRow>
    </TableBody>
  );
};

const areOptionsEqual = (prevOptions: IResultItem[], nextOptions: IResultItem[]) =>
  prevOptions.length === nextOptions.length && prevOptions.every((option, index) => option === nextOptions[index]);

const areFamiliesEqual = (prevFamilies: Props["families"], nextFamilies: Props["families"]) =>
  prevFamilies === nextFamilies || prevFamilies?.data?.families === nextFamilies?.data?.families;

export default memo(LightingParameters, (prevProps, nextProps) => {
  return (
    prevProps.tab === nextProps.tab &&
    prevProps.description === nextProps.description &&
    prevProps.optimizationType === nextProps.optimizationType &&
    prevProps.roadLength === nextProps.roadLength &&
    areFamiliesEqual(prevProps.families, nextProps.families) &&
    areOptionsEqual(prevProps.options, nextProps.options)
  );
});
